import type { AdminRole } from '../types/auth';
import type { AdminUsersListResponse, AdminUserDetail } from '../types/admin-user';
import type {
  ExpertProfile,
  ExpertProfileFilters,
  ExpertProfileFormData,
  ExpertProfilesListResponse,
  ExpertProfileStatus,
} from '../types/expert-profile';
import type {
  ExpertTip,
  ExpertTipFilterOptions,
  ExpertTipFilters,
  ExpertTipFormData,
  ExpertTipsListResponse,
  ExpertTipStatus,
} from '../types/expert-tip';
import { authenticatedFetch, formatApiError, getAdminToken, type ApiErrorResponse } from './apiClient';
import type {
  SparkActivityFilters,
  SparkActivitiesListResponse,
  SparkActivityDetail,
  SparkActivityBatch,
  SparkActivityFilterOptions,
  BatchUploadResult,
  UpdateSparkActivityPayload,
  ResolveMismatchPayload,
  CreateSparkActivityPayload,
} from '../types/spark-activity';
import type { PackName } from '../types/pack-name';
import type { EarlyAccessFilters, EarlyAccessListResponse } from '../types/early-access';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

/**
 * Admin login request
 */
export interface AdminLoginRequest {
  email: string;
  password: string;
}

/**
 * Admin login response
 */
export interface AdminLoginResponse {
  success: true;
  data: {
    token: string;
    admin: {
      id: number;
      email: string;
      name: string;
      role: AdminRole;
      expertProfileId: number | null;
    };
  };
  message: string;
  timestamp: string;
}

/**
 * Admin profile response
 */
export interface AdminProfileResponse {
  success: true;
  data: {
    id: number;
    email: string;
    name: string;
    role: AdminRole;
    expertProfileId: number | null;
    createdAt: string;
    updatedAt: string;
  };
  message: string;
  timestamp: string;
}

/**
 * Admin API service
 */
export const adminApi = {
  /**
   * Login admin user
   * @param credentials - Email and password
   * @returns Login response with token and admin data
   * @throws Error if login fails
   */
  async login(credentials: AdminLoginRequest): Promise<AdminLoginResponse> {
    const response = await fetch(`${API_BASE_URL}/admin/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Login failed');
    }

    return data as AdminLoginResponse;
  },

  /**
   * Get current admin profile
   * @param token - JWT access token
   * @returns Admin profile data
   * @throws Error if request fails
   */
  async getProfile(token: string): Promise<AdminProfileResponse> {
    const response = await fetch(`${API_BASE_URL}/admin/auth/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch profile');
    }

    return data as AdminProfileResponse;
  },
};

// Response type wrapper used across admin APIs
type ApiResponse<T> = { success: boolean; data: T; message: string };

// ─── Expert Profile API ─────────────────────────────────────────────────────

type ExpertProfileDetailsResponse = ApiResponse<ExpertProfile>;
type CreateExpertProfileResponse = ApiResponse<ExpertProfile>;
type ActiveExpertProfilesResponse = ApiResponse<ExpertProfile[]>;

export const expertProfileApi = {
  async listExpertProfiles(
    filters: Partial<ExpertProfileFilters>
  ): Promise<ExpertProfilesListResponse> {
    const params = new URLSearchParams();

    if (filters.status) params.append('status', filters.status);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const queryString = params.toString();
    const url = `/admin/expert-profiles${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url);
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch expert profiles');
    }

    const apiResponse = data as ApiResponse<ExpertProfilesListResponse>;
    return apiResponse.data;
  },

  async getExpertProfile(id: string): Promise<ExpertProfileDetailsResponse> {
    const response = await authenticatedFetch(`/admin/expert-profiles/${id}`);
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch expert profile');
    }

    return data;
  },

  async createExpertProfile(
    formData: ExpertProfileFormData
  ): Promise<CreateExpertProfileResponse> {
    const payload = {
      name: formData.name,
      subtitle: formData.subtitle,
      avatarDocumentId: formData.avatarDocumentId || null,
    };

    const response = await authenticatedFetch('/admin/expert-profiles', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to create expert profile');
    }

    return data;
  },

  async updateExpertProfile(
    id: string,
    formData: ExpertProfileFormData
  ): Promise<ExpertProfileDetailsResponse> {
    const payload = {
      name: formData.name,
      subtitle: formData.subtitle,
      avatarDocumentId: formData.avatarDocumentId || null,
    };

    const response = await authenticatedFetch(`/admin/expert-profiles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to update expert profile');
    }

    return data;
  },

  async updateExpertProfileStatus(
    id: string,
    status: ExpertProfileStatus
  ): Promise<ExpertProfileDetailsResponse> {
    const response = await authenticatedFetch(`/admin/expert-profiles/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to update expert profile status');
    }

    return data;
  },

  async getActiveProfiles(): Promise<ActiveExpertProfilesResponse> {
    const response = await authenticatedFetch('/admin/expert-profiles/active');
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch active expert profiles');
    }

    return data;
  },
};

// ─── Expert Tip API ──────────────────────────────────────────────────────────

type ExpertTipDetailsResponse = ApiResponse<ExpertTip>;
type CreateExpertTipResponse = ApiResponse<ExpertTip>;

function toExpertTipPayload(formData: ExpertTipFormData) {
  return {
    title: formData.title,
    body: formData.body,
    expertId: parseInt(formData.expertId, 10),
    sortOrder: formData.sortOrder.trim()
      ? parseInt(formData.sortOrder, 10)
      : null,
    linkResources: formData.linkResources.map((lr, index) => ({
      title: lr.title,
      url: lr.url,
      sortOrder: lr.sortOrder ?? index,
    })),
    domainId: formData.domainId ? parseInt(formData.domainId, 10) : null,
    skillIds: formData.skillIds,
  };
}

export const expertTipApi = {
  async listExpertTips(
    filters: Partial<ExpertTipFilters>
  ): Promise<ExpertTipsListResponse> {
    const params = new URLSearchParams();

    if (filters.expertId) params.append('expertId', filters.expertId);
    if (filters.status) params.append('status', filters.status);
    if (filters.domainId) params.append('domainId', filters.domainId);
    if (filters.domainSkillId) params.append('domainSkillId', filters.domainSkillId);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const queryString = params.toString();
    const url = `/admin/expert-tips${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url);
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch expert tips');
    }

    const apiResponse = data as ApiResponse<ExpertTipsListResponse>;
    return apiResponse.data;
  },

  async getFilterOptions(): Promise<ExpertTipFilterOptions> {
    const response = await authenticatedFetch('/admin/expert-tips/filters');
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch expert tip filter options');
    }

    const apiResponse = data as ApiResponse<ExpertTipFilterOptions>;
    return apiResponse.data;
  },

  async getExpertTip(id: string): Promise<ExpertTipDetailsResponse> {
    const response = await authenticatedFetch(`/admin/expert-tips/${id}`);
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch expert tip');
    }

    return data;
  },

  async createExpertTip(
    formData: ExpertTipFormData
  ): Promise<CreateExpertTipResponse> {
    const payload = toExpertTipPayload(formData);

    const response = await authenticatedFetch('/admin/expert-tips', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to create expert tip');
    }

    return data;
  },

  async updateExpertTip(
    id: string,
    formData: ExpertTipFormData
  ): Promise<ExpertTipDetailsResponse> {
    const payload = toExpertTipPayload(formData);

    const response = await authenticatedFetch(`/admin/expert-tips/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to update expert tip');
    }

    return data;
  },

  async updateExpertTipStatus(
    id: string,
    status: ExpertTipStatus
  ): Promise<ExpertTipDetailsResponse> {
    const response = await authenticatedFetch(`/admin/expert-tips/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to update expert tip status');
    }

    return data;
  },
};

// ─── Dashboard API ──────────────────────────────────────────────────────────

import type { DashboardStats } from '../types/dashboard';

type DashboardStatsResponse = ApiResponse<DashboardStats>;

/**
 * Dashboard API methods
 */
export const dashboardApi = {
  /**
   * Get aggregated dashboard statistics
   */
  async getStats(): Promise<DashboardStats> {
    const response = await authenticatedFetch('/admin/dashboard/stats');
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch dashboard stats');
    }

    const apiResponse = data as DashboardStatsResponse;
    return apiResponse.data;
  },
};

// ─── Admin User API ──────────────────────────────────────────────────────────

export const adminUserApi = {
  async listAdminUsers(): Promise<AdminUsersListResponse> {
    const response = await authenticatedFetch('/admin/users');
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch admin users');
    }

    const apiResponse = data as ApiResponse<AdminUsersListResponse>;
    return apiResponse.data;
  },

  async getAdminUser(id: string): Promise<ApiResponse<AdminUserDetail>> {
    const response = await authenticatedFetch(`/admin/users/${id}`);
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch admin user');
    }

    return data;
  },

  async assignProfile(
    userId: string,
    expertProfileId: number | null
  ): Promise<ApiResponse<AdminUserDetail>> {
    const response = await authenticatedFetch(`/admin/users/${userId}/assign-profile`, {
      method: 'PATCH',
      body: JSON.stringify({ expertProfileId }),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to assign profile');
    }

    return data;
  },
};

// ─── Communication Stage API ─────────────────────────────────────────────────

import type { CommunicationStage, CommunicationStageFormData } from '../types/communication-stage';
import type { Domain, DomainFormData } from '../types/domain';
import type { DomainSkill, DomainSkillFormData } from '../types/domain-skill';
import type { ChildPreference, ChildPreferenceFormData } from '../types/child-preference';
import type { SparkPlay, SparkPlayFormData } from '../types/spark-play';

export const communicationStageApi = {
  async listStages(): Promise<CommunicationStage[]> {
    const response = await authenticatedFetch('/admin/communication-stages');
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch communication stages');
    return (data as { success: true; data: CommunicationStage[] }).data;
  },

  async updateStage(id: number, formData: CommunicationStageFormData): Promise<CommunicationStage> {
    const response = await authenticatedFetch(`/admin/communication-stages/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(formData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to update communication stage');
    return (data as { success: true; data: CommunicationStage }).data;
  },
};

export const domainApi = {
  async listDomains(): Promise<Domain[]> {
    const response = await authenticatedFetch('/admin/domains');
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch domains');
    return (data as { success: true; data: Domain[] }).data;
  },

  async updateDomain(id: number, formData: DomainFormData): Promise<Domain> {
    const response = await authenticatedFetch(`/admin/domains/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(formData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to update domain');
    return (data as { success: true; data: Domain }).data;
  },
};

export const domainSkillApi = {
  async listSkills(params?: { domainId?: number }): Promise<DomainSkill[]> {
    const query = new URLSearchParams();
    if (params?.domainId) query.set('domainId', String(params.domainId));
    const qs = query.toString();
    const response = await authenticatedFetch(`/admin/domain-skills${qs ? `?${qs}` : ''}`);
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch domain skills');
    return (data as { success: true; data: DomainSkill[] }).data;
  },

  async updateSkill(id: number, formData: DomainSkillFormData): Promise<DomainSkill> {
    const response = await authenticatedFetch(`/admin/domain-skills/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(formData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to update domain skill');
    return (data as { success: true; data: DomainSkill }).data;
  },
};

export const childPreferenceApi = {
  async listPreferences(): Promise<ChildPreference[]> {
    const response = await authenticatedFetch('/admin/child-preferences');
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch child preferences');
    return (data as { success: true; data: ChildPreference[] }).data;
  },

  async updatePreference(id: number, formData: ChildPreferenceFormData): Promise<ChildPreference> {
    const response = await authenticatedFetch(`/admin/child-preferences/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(formData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to update child preference');
    return (data as { success: true; data: ChildPreference }).data;
  },
};

export const sparkPlayApi = {
  async listPlays(): Promise<SparkPlay[]> {
    const response = await authenticatedFetch('/admin/spark-plays');
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch spark plays');
    return (data as { success: true; data: SparkPlay[] }).data;
  },

  async updatePlay(id: number, formData: SparkPlayFormData): Promise<SparkPlay> {
    const response = await authenticatedFetch(`/admin/spark-plays/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(formData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to update spark play');
    return (data as { success: true; data: SparkPlay }).data;
  },
};

// ─── Early Access API ────────────────────────────────────────────────────────

type EarlyAccessApiResponse = { success: boolean; data: EarlyAccessListResponse; message: string };

export const earlyAccessApi = {
  async listSignups(filters: Partial<EarlyAccessFilters>): Promise<EarlyAccessListResponse> {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const queryString = params.toString();
    const url = `/admin/early-access${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url);
    const data = await response.json();

    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(formatApiError(errorData.error, response.status) || 'Failed to fetch early access signups');
    }

    const apiResponse = data as EarlyAccessApiResponse;
    return apiResponse.data;
  },
};

// ─── Spark Activity API ─────────────────────────────────────────────────────

export const sparkActivityApi = {
  async listActivities(filters: Partial<SparkActivityFilters>): Promise<SparkActivitiesListResponse> {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.domainId) params.set('domainId', String(filters.domainId));
    if (filters.skillId) params.set('skillId', String(filters.skillId));
    params.set('status', filters.status ?? 'all');
    if (filters.batchId) params.set('batchId', String(filters.batchId));
    const qs = params.toString();
    const response = await authenticatedFetch(`/admin/spark-activities${qs ? `?${qs}` : ''}`);
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch spark activities');
    return (data as { success: true; data: SparkActivitiesListResponse }).data;
  },

  async getActivity(id: number): Promise<SparkActivityDetail> {
    const response = await authenticatedFetch(`/admin/spark-activities/${id}`);
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch spark activity');
    return (data as { success: true; data: SparkActivityDetail }).data;
  },

  async updateStatus(id: number, status: string): Promise<void> {
    const response = await authenticatedFetch(`/admin/spark-activities/${id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to update status');
  },

  async uploadCsv(file: File): Promise<BatchUploadResult> {
    const token = getAdminToken();
    if (!token) throw new Error('Not authenticated');
    const formData = new FormData();
    formData.append('file', file);
    const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
    const response = await fetch(`${apiBase}/admin/spark-activities/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to upload CSV');
    return (data as { success: true; data: BatchUploadResult }).data;
  },

  async getBatches(): Promise<SparkActivityBatch[]> {
    const response = await authenticatedFetch('/admin/spark-activities/batches');
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch batches');
    return (data as { success: true; data: SparkActivityBatch[] }).data;
  },

  async getFilterOptions(): Promise<SparkActivityFilterOptions> {
    const response = await authenticatedFetch('/admin/spark-activities/filters');
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch filter options');
    return (data as { success: true; data: SparkActivityFilterOptions }).data;
  },

  async updateActivity(id: number, payload: UpdateSparkActivityPayload): Promise<SparkActivityDetail> {
    const response = await authenticatedFetch(`/admin/spark-activities/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    const json = await response.json();
    if (!response.ok) throw new Error(formatApiError((json as ApiErrorResponse).error, response.status) || 'Failed to update activity');
    return (json as { success: true; data: SparkActivityDetail }).data;
  },

  async resolveMismatch(activityId: number, data: ResolveMismatchPayload): Promise<void> {
    const response = await authenticatedFetch(`/admin/spark-activities/${activityId}/mismatch/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await response.json();
    if (!response.ok) throw new Error(formatApiError((json as ApiErrorResponse).error, response.status) || 'Failed to resolve mismatch');
  },

  async createActivity(payload: CreateSparkActivityPayload): Promise<{ id: number }> {
    const response = await authenticatedFetch('/admin/spark-activities', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to create activity');
    return (data as { success: true; data: { id: number } }).data;
  },

  async checkKeyAvailability(key: string): Promise<{ available: boolean }> {
    const response = await authenticatedFetch(`/admin/spark-activities/check-key?key=${encodeURIComponent(key)}`);
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to check key');
    return (data as { success: true; data: { available: boolean } }).data;
  },

  async deleteActivity(id: number): Promise<void> {
    const response = await authenticatedFetch(`/admin/spark-activities/${id}`, {
      method: 'DELETE',
    });
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to delete activity');
  },

  async deleteBatch(batchId: number): Promise<{ deletedCount: number }> {
    const response = await authenticatedFetch(`/admin/spark-activities/batch/${batchId}`, {
      method: 'DELETE',
    });
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to delete batch');
    return (data as { success: true; data: { deletedCount: number } }).data;
  },

  async downloadCsv(): Promise<void> {
    const token = getAdminToken();
    if (!token) throw new Error('Not authenticated');

    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
    const response = await fetch(`${baseUrl}/admin/spark-activities/download-csv`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) throw new Error('Failed to download CSV');

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `spark-activities-export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};

export const packNameApi = {
  async listPackNames(): Promise<PackName[]> {
    const response = await authenticatedFetch('/admin/pack-names');
    const data = await response.json();
    if (!response.ok) throw new Error(formatApiError((data as ApiErrorResponse).error, response.status) || 'Failed to fetch pack names');
    return (data as { success: true; data: PackName[] }).data;
  },
};
